import pako from 'pako';
import mitt from 'mitt';
import models from '@/models';
import { myClearInterval, mySetInterval } from '@/utils/fn';

export type SocketType = 'MARKET' | 'SYMBOL' | 'USER';

const PING_INTERVAL = 5000;
const MAX_PING_TRY_TIMES = 2;
const MAX_RECONNECT_TIMES = 10;
const HEARTBEAT_INTERVAL = 10000;

function formatTopic(topic: string): string {
  return topic;
}
class SocketClient {
  client?: WebSocket;

  type: SocketType;

  count = 0;

  messageListener = mitt();

  clientListener = mitt();

  url: string;

  heartbeatInterval: any;

  emitDataFormatter: (params: any) => any = (params: any) => params;

  delayEmits: Record<string, any> = {};

  private pendingPingCount = 0;

  private emitId = 0;

  reconnectCount: any;

  constructor(url: string, type: SocketType, emitDataFormatter?: (params: any) => any) {
    this.url = url;
    this.type = type;
    this.heartbeatInterval = null;

    if (emitDataFormatter) {
      this.emitDataFormatter = emitDataFormatter;
    }

    this.init();
  }

  onReady(callback: mitt.Handler): void {
    if (this.client) {
      callback();
    }
    this.clientListener.on('open', callback);
  }

  on(topic: string, callback: mitt.Handler): void {
    this.messageListener.on(topic, callback);
  }

  off(topic: string, callback: mitt.Handler) {
    this.messageListener.off(topic, callback);
  }

  emit(req: string, data?: any) {
    if (this.client) {
      const emitData = req === 'auth' ? { token: data } : this.emitDataFormatter(data);
      this.client.send(JSON.stringify({ req, ...emitData }));
    } else {
      this.delayEmits[req] = data;
    }
  }

  onClient(eventName: string, callback: mitt.Handler): void {
    this.clientListener.on(eventName, callback);
  }

  offClient(eventName: string, callback: mitt.Handler): void {
    this.clientListener.off(eventName, callback);
  }

  sendPong(): void {
    this.client?.send(
      JSON.stringify({
        method: 'PONG',
        E: new Date().getTime(),
      }),
    );
  }

  heartbeat() {
    this.clearHeartbeat();

    this.heartbeatInterval = mySetInterval(() => {
      if (this.client) {
        this.client?.send('ping');
      }
    }, HEARTBEAT_INTERVAL);
  }

  clearHeartbeat() {
    const interval = this.heartbeatInterval;
    if (interval) {
      myClearInterval(interval);
    }
  }

  private loopingPing() {
    setInterval(() => {
      if (this.pendingPingCount > MAX_PING_TRY_TIMES) {
        this.disconnect();
        return;
      }
      if (this.client) {
        this.client?.send(
          JSON.stringify({
            ping: new Date().getTime(),
          }),
        );
        this.pendingPingCount += 1;
      }
    }, PING_INTERVAL);
  }

  disconnect() {
    this.clearHeartbeat();
    this.client?.close();
    this.client = undefined;
    this.init();
  }

  init() {
    this.pendingPingCount = 0;
    if (this.client) {
      this.client.close();
      return;
    }
    const client = new WebSocket(this.url);

    client.addEventListener('open', () => {
      this.client = client;
      this.clientListener.emit('open');
      this.fireAllDelayEmits();

      if (this.reconnectCount > 0) {
        switch (this.type) {
          case 'MARKET':
            this.emitAboutMarket();
            break;
          case 'SYMBOL':
            this.emitAboutSymbol(sessionStorage.getItem('symbol') || '', models.futures.currentDepthMergeStep);
            break;
          case 'USER':
            this.emitAboutUser(sessionStorage.getItem('listenKey') || '');
            break;
          default:
          // do nothing
        }
      }

      this.reconnectCount = 0;
    });

    client.addEventListener('message', msg => {
      let data = msg.data;
      if (data && data !== 'null') {
        // pong
        // if (/pong/i.test(data)) {
        //   this.pendingPingCount = 0;
        //   return;
        // }
        // ping
        if (/ping/.test(data)) {
          this.sendPong();
          return;
        }

        if (data instanceof Blob) {
          const reader = new FileReader();
          reader.readAsArrayBuffer(data);
          reader.onload = evt => {
            if (evt?.target?.readyState === FileReader.DONE && evt?.target?.result) {
              const result = new Uint8Array(evt.target?.result as any);
              const resultData = JSON.parse(pako.inflate(result, { to: 'string' }));
              const formattedTopic = formatTopic(resultData.type);
              resultData.req = formattedTopic;
              this.messageListener.emit(formattedTopic, resultData);
            }
          };
        } else {
          try {
            data = JSON.parse(data);
            const channel = data.channel;
            this.messageListener.emit(channel, data);
          } catch (e) {
            if (process.env.NODE_ENV === 'development') {
              // console.log(`ws消息'${data}'不能格式化为JSON`);
            }

            // 额外的消息
            this.messageListener.emit('news', data);
          }
        }
      }
      this.clientListener.emit('message', data);
    });

    client.addEventListener('close', () => {
      this.clientListener.emit('close');
      this.reconnectCount += 1;
      this.client = undefined;

      const symbol = models.ticker.currentSymbolIdentity;
      const isLogin = models.account.isLogin;
      const positionSymbol = models.futures.isShowCurrentSymbolOnly ? symbol : undefined;

      switch (this.type) {
        case 'MARKET':
          // do nothing
          break;
        case 'SYMBOL':
          // 断线是立即拉取深度和成交信息，避免价格展示倒挂
          if (symbol) {
            models.futures.fetchDealData({ symbol });
            models.futures.fetchDepthData({ symbol, level: 30, mergeStep: models.futures.currentDepthMergeStep });
          }
          break;
        case 'USER':
          if (isLogin && symbol) {
            models.position.fetchPositionList();
            // 断线期间的体验仓开平仓事件会丢失，重连后整体替换快照
            models.position.fetchExperiencePositionList();
            models.entrust.fetchProfitList({
              symbol: positionSymbol,
              state: 'UNFINISHED',
            });
            models.order.fetchOrderList({
              symbol: positionSymbol,
              state: 'UNFINISHED',
            });
            models.entrust.fetchPlanList({
              symbol: positionSymbol,
              state: 'UNFINISHED',
            });
          }
          break;
        default:
        // do nothing
      }

      setTimeout(
        () => {
          this.init();
        },
        this.reconnectCount > MAX_RECONNECT_TIMES ? 30000 : 1000,
      );
    });

    client.addEventListener('error', err => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });

    this.heartbeat();
  }

  private fireAllDelayEmits(): void {
    Object.keys(this.delayEmits).forEach(topic => {
      if (!this.client) return;
      const data = this.delayEmits[topic];
      this.client.send(JSON.stringify({ req: topic, ...this.emitDataFormatter(data) }));
      delete this.delayEmits[topic];
    });
  }

  emitAboutMarket() {
    this.emit('sub_ticker');
    this.emit('sub_mark_price');
    // this.emit('sub_index_price');

    const klineOptionStorage = sessionStorage.getItem('sub_kline');
    if (klineOptionStorage) {
      try {
        const option = JSON.parse(klineOptionStorage);
        this.emit('sub_kline', option);
      } catch (e) {
        // do nothing
      }
    }
  }

  unEmitAboutMarket() {
    this.emit('unsub_ticker');
    this.emit('unsub_mark_price');
    // this.emit('unsub_index_price');
    this.emit('unsub_kline');
  }

  emitAboutSymbol(symbol: string, mergeStep?: string) {
    if (symbol) {
      this.emit('sub_symbol', mergeStep ? { symbol, mergeStep } : { symbol });
    }
  }

  unEmitAboutSymbol() {
    this.emit('unsub_symbol');
  }

  emitAboutUser(listenKey?: string | number) {
    if (listenKey) {
      this.emit('sub_user', { listenKey });
    }
  }

  unEmitAboutUser() {
    this.emit('unsub_user');
  }
}

export default SocketClient;
